import type { Area, Project, ProjectStatus, Task } from "@hq/contracts";
import { newId } from "./ids";
import type { NowContext } from "./time";

export function createProject(
  input: { id?: string; name: string; area?: string; notesFolderId?: string; sortOrder?: number },
  ctx: NowContext,
): Project {
  const name = input.name.trim();
  if (!name) throw new Error("Project name is required");
  const project: Project = {
    id: input.id ?? newId(),
    createdAt: ctx.now,
    updatedAt: ctx.now,
    revision: 0,
    name,
    status: "active",
    sortOrder: input.sortOrder ?? Date.parse(ctx.now),
  };
  if (input.notesFolderId) project.notesFolderId = input.notesFolderId;
  if (input.area?.trim()) project.area = input.area.trim();
  return project;
}

export function renameProject(project: Project, name: string, ctx: NowContext): Project {
  const n = name.trim();
  if (!n) throw new Error("Project name is required");
  return { ...project, name: n, updatedAt: ctx.now };
}

/** Archiving never touches notes or tasks; it only hides the project from active lists. */
export function setProjectStatus(
  project: Project,
  status: ProjectStatus,
  ctx: NowContext,
): Project {
  return { ...project, status, updatedAt: ctx.now };
}

export function setProjectNotesFolder(
  project: Project,
  notesFolderId: string | null,
  ctx: NowContext,
): Project {
  const next: Project = { ...project, updatedAt: ctx.now };
  if (notesFolderId === null) delete next.notesFolderId;
  else next.notesFolderId = notesFolderId;
  return next;
}

export function reorderProject(project: Project, sortOrder: number, ctx: NowContext): Project {
  return { ...project, sortOrder, updatedAt: ctx.now };
}

export function deleteProject(project: Project, ctx: NowContext): Project {
  return { ...project, deletedAt: ctx.now, updatedAt: ctx.now };
}

export function restoreProject(project: Project, ctx: NowContext): Project {
  const next: Project = { ...project, updatedAt: ctx.now };
  delete next.deletedAt;
  return next;
}

export function sortProjects(projects: readonly Project[]): Project[] {
  return [...projects].sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name));
}

export function setProjectArea(project: Project, area: string | null, ctx: NowContext): Project {
  const next = { ...project, updatedAt: ctx.now };
  const name = area?.trim();
  if (name) next.area = name;
  else delete next.area;
  return next;
}

/** Standalone projects first, then areas alphabetically; retain manual order within each. */
export function groupProjectsByArea(projects: readonly Project[], areas: readonly Area[] = []) {
  const groups = new Map<string, Project[]>();
  for (const area of areas) if (!area.deletedAt) groups.set(area.name, []);
  for (const project of sortProjects(projects)) {
    if (project.deletedAt || project.status !== "active") continue;
    const area = project.area ?? "";
    const group = groups.get(area) ?? [];
    group.push(project);
    groups.set(area, group);
  }
  return [...groups].sort(([a], [b]) => (a === "" ? -1 : b === "" ? 1 : a.localeCompare(b)));
}

/** Count every non-deleted todo, including someday tasks and subtasks, once. */
export function projectProgress(
  tasks: readonly Task[],
): Map<string, { completed: number; total: number }> {
  const progress = new Map<string, { completed: number; total: number }>();
  for (const task of tasks) {
    if (!task.projectId || task.deletedAt) continue;
    const count = progress.get(task.projectId) ?? { completed: 0, total: 0 };
    count.total++;
    if (task.status === "completed") count.completed++;
    progress.set(task.projectId, count);
  }
  return progress;
}

export function createArea(input: { id?: string; name: string }, ctx: NowContext): Area {
  const name = input.name.trim();
  if (!name) throw new Error("Area name is required");
  return { id: input.id ?? newId(), name, createdAt: ctx.now, updatedAt: ctx.now, revision: 0 };
}
